import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  Check as CheckIcon,
  KeyboardArrowDown as ChevronDownIcon,
  KeyboardArrowUp as ChevronUpIcon,
  PriorityHighRounded as ExclamationIcon,
} from "@mui/icons-material";
import { CARD_THEMES } from "./helpers";

export default function TrainerSelectionForm({
  trainersLoading,
  trainersList,
  selectedTrainer,
  onSelectTrainer,
  remark,
  onChangeRemark,
}) {
  const [trainerSearch, setTrainerSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(true);

  const filteredTrainers = (trainersList || []).filter((t) => {
    const term = trainerSearch.toLowerCase().trim();
    if (!term) return true;
    const nameMatch = t.fullname.toLowerCase().includes(term);
    const fdpMatch = t.fdpDomains?.some((d) => d.toLowerCase().includes(term));
    const techcampMatch = t.techcampDomains?.some((d) =>
      d.toLowerCase().includes(term)
    );
    return nameMatch || fdpMatch || techcampMatch;
  });

  return (
    <>
      {/* Warning Notice Banner */}
      <Box
        sx={{
          backgroundColor: "#FFFBEB",
          border: "1px solid #FEF3C7",
          borderRadius: "10px",
          p: 1.75,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            backgroundColor: "#F59E0B",
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <ExclamationIcon sx={{ fontSize: 16 }} />
        </Box>
        <Typography
          sx={{
            fontFamily: '"Poppins", sans-serif',
            fontSize: "0.82rem",
            color: "#B45309",
            fontWeight: 500,
            lineHeight: 1.45,
          }}
        >
          If you change the trainer, you will have to pay the travel and stay allowances for the new trainer.
        </Typography>
      </Box>

      {/* Select Trainer Form Field */}
      <Box>
        <Typography
          sx={{
            fontFamily: '"Poppins", sans-serif',
            fontSize: "0.84rem",
            fontWeight: 600,
            color: "#1E293B",
            mb: 1,
          }}
        >
          Select Trainer <span style={{ color: "#EF4444" }}>*</span>
        </Typography>

        {/* Dropdown Container */}
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #CBD5E1",
            borderRadius: "10px",
            overflow: "hidden",
            backgroundColor: "#FFFFFF",
          }}
        >
          {/* Trigger / Summary Bar */}
          <Box
            onClick={() => setDropdownOpen((prev) => !prev)}
            sx={{
              p: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              backgroundColor: "#FFFFFF",
              "&:hover": { backgroundColor: "#F8FAFC" },
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Poppins", sans-serif',
                fontSize: "0.88rem",
                color: selectedTrainer ? "#0F172A" : "#94A3B8",
                fontWeight: selectedTrainer ? 600 : 400,
              }}
            >
              {selectedTrainer ? selectedTrainer.fullname : "Select a trainer"}
            </Typography>
            {dropdownOpen ? (
              <ChevronUpIcon sx={{ fontSize: 20, color: "#64748B" }} />
            ) : (
              <ChevronDownIcon sx={{ fontSize: 20, color: "#64748B" }} />
            )}
          </Box>

          {/* Expandable Dropdown List with Search */}
          {dropdownOpen && (
            <Box
              sx={{
                borderTop: "1px solid #E2E8F0",
                p: 1.5,
                backgroundColor: "#F8FAFC",
              }}
            >
              {/* Search Input Box */}
              <TextField
                fullWidth
                size="small"
                placeholder="Search trainer by name or domains..."
                value={trainerSearch}
                onChange={(e) => setTrainerSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 19, color: "#94A3B8" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 1.5,
                  "& .MuiOutlinedInput-root": {
                    fontFamily: '"Poppins", sans-serif',
                    borderRadius: "8px",
                    backgroundColor: "#FFFFFF",
                    fontSize: "0.84rem",
                    "& fieldset": { borderColor: "#E2E8F0" },
                    "&:hover fieldset": { borderColor: "#CBD5E1" },
                    "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
                  },
                }}
              />

              {/* Trainers Option List */}
              <Box
                sx={{
                  maxHeight: "340px",
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                  pr: 0.5,
                  scrollbarWidth: "thin",
                  "&::-webkit-scrollbar": { width: "5px" },
                  "&::-webkit-scrollbar-track": { backgroundColor: "#F1F5F9" },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#CBD5E1",
                    borderRadius: "4px",
                  },
                }}
              >
                {trainersLoading && trainersList.length === 0 ? (
                  <Box
                    sx={{
                      py: 3,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <CircularProgress size={18} sx={{ color: "#2563EB" }} />
                    <Typography
                      sx={{
                        fontFamily: '"Poppins", sans-serif',
                        color: "#64748B",
                        fontSize: "0.82rem",
                      }}
                    >
                      Loading trainers...
                    </Typography>
                  </Box>
                ) : filteredTrainers.length === 0 ? (
                  <Typography
                    sx={{
                      fontFamily: '"Poppins", sans-serif',
                      textAlign: "center",
                      py: 3,
                      color: "#94A3B8",
                      fontSize: "0.82rem",
                    }}
                  >
                    No trainers match your search.
                  </Typography>
                ) : (
                  filteredTrainers.map((t, index) => {
                    const isSelected = selectedTrainer?.id === t.id;
                    const theme = CARD_THEMES[index % CARD_THEMES.length];

                    return (
                      <Paper
                        key={t.id}
                        elevation={0}
                        onClick={() => onSelectTrainer(t)}
                        sx={{
                          p: 1.5,
                          borderRadius: "10px",
                          cursor: "pointer",
                          transition: "all 0.15s ease-in-out",
                          backgroundColor: isSelected ? "#EFF6FF" : theme.bg,
                          border: isSelected
                            ? "1.5px solid #2563EB"
                            : `1px solid ${theme.border}`,
                          borderLeft: isSelected
                            ? "5px solid #2563EB"
                            : `5px solid ${theme.accent}`,
                          boxShadow: isSelected
                            ? "0 3px 10px rgba(37, 99, 235, 0.15)"
                            : "0 1px 3px rgba(0,0,0,0.03)",
                          "&:hover": {
                            borderColor: isSelected ? "#2563EB" : theme.accent,
                            borderLeft: isSelected
                              ? "5px solid #1D4ED8"
                              : `5px solid ${theme.accent}`,
                            backgroundColor: isSelected ? "#EFF6FF" : theme.hoverBg,
                            boxShadow: "0 3px 8px rgba(0,0,0,0.06)",
                          },
                        }}
                      >
                        {/* Header: Trainer Name & Badge */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 0.75,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography
                              sx={{
                                fontFamily: '"Poppins", sans-serif',
                                fontWeight: 700,
                                fontSize: "0.92rem",
                                color: isSelected ? "#1D4ED8" : "#0F172A",
                              }}
                            >
                              {t.fullname}
                            </Typography>
                          </Box>
                          {isSelected && (
                            <Box
                              sx={{
                                width: 22,
                                height: 22,
                                borderRadius: "50%",
                                backgroundColor: "#2563EB",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#FFFFFF",
                                flexShrink: 0,
                              }}
                            >
                              <CheckIcon sx={{ fontSize: 15 }} />
                            </Box>
                          )}
                        </Box>

                        {/* Domain Chips */}
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                          {t.fdpDomains && t.fdpDomains.length > 0 && (
                            <Box>
                              <Typography
                                sx={{
                                  fontFamily: '"Poppins", sans-serif',
                                  fontSize: "0.68rem",
                                  fontWeight: 700,
                                  color: "#2563EB",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.4px",
                                  mb: 0.4,
                                }}
                              >
                                FDP Domains
                              </Typography>
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {t.fdpDomains.map((dom, dIdx) => (
                                  <Chip
                                    key={dIdx}
                                    label={dom}
                                    size="small"
                                    sx={{
                                      fontFamily: '"Poppins", sans-serif',
                                      backgroundColor: "#EFF6FF",
                                      color: "#1E40AF",
                                      border: "1px solid #DBEAFE",
                                      fontWeight: 500,
                                      fontSize: "0.7rem",
                                      height: "22px",
                                      borderRadius: "6px",
                                      "& .MuiChip-label": { px: 0.75, py: 0 },
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}

                          {t.techcampDomains && t.techcampDomains.length > 0 && (
                            <Box>
                              <Typography
                                sx={{
                                  fontFamily: '"Poppins", sans-serif',
                                  fontSize: "0.68rem",
                                  fontWeight: 700,
                                  color: "#7C3AED",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.4px",
                                  mb: 0.4,
                                }}
                              >
                                TechCamp Domains
                              </Typography>
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {t.techcampDomains.map((dom, dIdx) => (
                                  <Chip
                                    key={dIdx}
                                    label={dom}
                                    size="small"
                                    sx={{
                                      fontFamily: '"Poppins", sans-serif',
                                      backgroundColor: "#FAF5FF",
                                      color: "#6B21A8",
                                      border: "1px solid #F3E8FF",
                                      fontWeight: 500,
                                      fontSize: "0.7rem",
                                      height: "22px",
                                      borderRadius: "6px",
                                      "& .MuiChip-label": { px: 0.75, py: 0 },
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      </Paper>
                    );
                  })
                )}
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Remark Form Field */}
      <Box>
        <Typography
          sx={{
            fontFamily: '"Poppins", sans-serif',
            fontSize: "0.84rem",
            fontWeight: 600,
            color: "#1E293B",
            mb: 1,
          }}
        >
          Remark{" "}
          <span style={{ color: "#94A3B8", fontWeight: 400, fontSize: "0.8rem" }}>
            (Optional)
          </span>
        </Typography>
        <TextField
          multiline
          rows={3}
          fullWidth
          placeholder="Please write your remark here (optional)..."
          value={remark}
          onChange={(e) => onChangeRemark(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              fontFamily: '"Poppins", sans-serif',
              borderRadius: "10px",
              backgroundColor: "#FFFFFF",
              fontSize: "0.88rem",
              "& fieldset": { borderColor: "#CBD5E1" },
              "&:hover fieldset": { borderColor: "#94A3B8" },
              "&.Mui-focused fieldset": { borderColor: "#3B82F6" },
            },
          }}
        />
      </Box>
    </>
  );
}
